import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const SECTIONS = [
  {
    id: 'overview',
    title: '1. Who We Are',
    body: [
      'AB8 Beauty ("AB8 Beauty", "we", "us") is a United States-based skincare company. This Privacy Policy explains how we collect, use, disclose, and protect personal information when you use our website (ab8beauty.com) or purchase our products.',
      'We are committed to handling your personal information responsibly and in accordance with applicable U.S. privacy laws, including the California Consumer Privacy Act (CCPA) where it applies to you.',
    ],
  },
  {
    id: 'what-we-collect',
    title: '2. Personal Information We Collect',
    body: [
      'Account information: name and email address when you register.',
      'Order information: shipping address, order details, and payment method metadata (we do not store full card numbers — payment is handled by Stripe, PCI-DSS Level 1 certified).',
      'Referral data: your referral code, the customers you refer, and your earned referral cash history.',
      'Technical data: IP address, browser type, device identifier, pages visited, and session duration, collected via server logs and first-party analytics.',
      'Communications: any emails, support tickets, or messages you send us.',
    ],
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    body: [
      'To fulfil orders and send shipping notifications.',
      'To create and maintain your customer account.',
      'To calculate, credit, and display your Referral Cash balance from the referral program.',
      'To send transactional emails (order confirmations, password resets, and customer support replies).',
      'To improve site functionality and detect fraud.',
      'To comply with applicable legal and tax obligations.',
      'We do not use your information for advertising to third parties or sell it to data brokers.',
    ],
  },
  {
    id: 'sharing',
    title: '4. Sharing & Disclosure',
    body: [
      'Service providers: Stripe (payment processing), shipping carriers (name and address for delivery), and our email provider (transactional notifications). All processors are contractually bound to use your data only for the services they provide to us.',
      'Legal requirements: We may disclose information to law enforcement or regulators where required by applicable law, a valid court order, or to protect the rights and safety of AB8 Beauty and its customers.',
      'Business transfers: In the event of a merger or acquisition, personal data may be transferred subject to the same privacy protections described here.',
      'We do not sell, rent, or trade your personal information with third parties for marketing purposes.',
    ],
  },
  {
    id: 'cookies',
    title: '5. Cookies & Tracking',
    body: [
      'We use strictly necessary cookies to maintain your session and shopping cart. We do not use third-party advertising cookies.',
      'We use first-party analytics (server-side) to understand aggregate traffic patterns. No fingerprinting or cross-site tracking is performed.',
      'You can disable cookies in your browser settings, but this may prevent the site from functioning correctly.',
    ],
  },
  {
    id: 'retention',
    title: '6. Data Retention',
    body: [
      'Account and order data is retained as long as needed to provide our services and for the period required by applicable tax and recordkeeping laws.',
      'If you request account deletion, we will anonymise your personal identifiers within 30 days, retaining only aggregate transactional records required for compliance. Requests may be submitted to privacy@ab8beauty.com.',
    ],
  },
  {
    id: 'rights',
    title: '7. Your Privacy Rights',
    body: [
      'Depending on where you live, you may have the right to: (a) know what personal information we hold about you; (b) request corrections to inaccurate information; (c) request deletion of your personal information; and (d) opt out of the sale of your personal information (note: we do not sell your personal information).',
      'To exercise these rights, email privacy@ab8beauty.com. We will respond within the timeframe required by applicable law (generally within 30–45 days).',
    ],
  },
  {
    id: 'security',
    title: '8. Security',
    body: [
      'We use TLS encryption for all data in transit. Passwords are hashed using Argon2. Database backups are encrypted at rest. Payment data is handled entirely by Stripe and never stored on our servers.',
      'Despite our safeguards, no system is 100% secure. If you suspect unauthorised access, contact support@ab8beauty.com immediately.',
    ],
  },
  {
    id: 'changes',
    title: '9. Changes to This Policy',
    body: [
      'We may update this Privacy Policy periodically. Material changes will be notified by email and/or a site notice at least 14 days before they take effect. The effective date at the top of this page reflects the most recent version.',
      'Last updated: January 2025.',
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <section className="bg-tea-700 text-white py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Legal</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Privacy Policy</h1>
          <p className="mt-3 text-tea-200">Last updated January 2025 · AB8 Beauty · United States</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 flex gap-12">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            <nav className="flex flex-col gap-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="text-sm text-gray-500 hover:text-tea-700 transition-colors py-1 leading-tight"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="flex flex-col gap-10">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{s.title}</h2>
                {s.body.map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed mb-3">{para}</p>
                ))}
              </section>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-4">
              Privacy questions? Email <a href="mailto:privacy@ab8beauty.com" className="text-tea-700 underline">privacy@ab8beauty.com</a>
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/terms" className="text-tea-700 font-medium hover:underline text-sm">Terms of Service</Link>
              <Link to="/compliance" className="text-tea-700 font-medium hover:underline text-sm">Ingredients & Safety</Link>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
